<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { closesOnBack } from '@/lib/backCloses.js'
import { takeLayer } from '@/lib/layers.js'

const props = defineProps({ open: Boolean, title: { type: String, default: '' }, width: { type: String, default: 'max-w-lg' } })
const emit = defineEmits(['close'])
// Escape closes the drawer, not while a dialog opened over it is up (that one closes first).
function onKey(e) {
  if (e.key === 'Escape' && props.open && document.querySelectorAll('[role="dialog"][aria-modal="true"]').length <= 1) emit('close')
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
// Back on a phone closes it, not the page under it.
let release = null
// Its layer among everything open over the page, taken as it opens (lib/layers.js).
const layer = ref(null)
watch(() => props.open, (open) => {
  release?.()
  release = open ? closesOnBack(() => emit('close')) : null
  layer.value?.release()
  layer.value = open ? takeLayer() : null
}, { immediate: true })
onBeforeUnmount(() => {
  release?.()
  layer.value?.release()
})
</script>

<template>
  <Teleport to="body">
    <Transition enter-active-class="transition duration-200" enter-from-class="opacity-0" leave-active-class="transition duration-150" leave-to-class="opacity-0">
      <div v-if="open" class="fixed inset-0 flex justify-end bg-slate-900/30" :style="{ zIndex: layer?.z ?? 50 }" @mousedown.self="emit('close')">
        <aside role="dialog" aria-modal="true" :aria-label="title" :class="['surface flex h-full w-full flex-col shadow-2xl', width]">
          <header class="flex items-center gap-3 border-b border-default px-4 py-3">
            <slot name="header"><h2 class="min-w-0 flex-1 truncate text-base font-semibold">{{ title }}</h2></slot>
            <button type="button" class="rounded-md p-1.5 text-faint hover:bg-[var(--surface-3)]" :aria-label="$t('common.close')" @click="emit('close')">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto"><slot /></div>
          <footer v-if="$slots.footer" class="border-t border-default px-4 pt-3 safe-bottom-pad"><slot name="footer" /></footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>
